<template>
  <div class="clubs-listing-page" :style="themeStyles">
    <header class="page-header">
      <h1>Tulum Clubs Directory</h1>

    </header>
    <div class="card text-center">
      <p>We put together the list of the clubs with current activity in Tulum</p>

      <p>Drop us a line if you want your club to be listed here.</p>
    </div>

    <div class="filters-container">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search for a club by name..."
        class="search-input"
      />
      <div class="filter-dropdowns">
        <select v-model="selectedMusic" class="filter-select">
          <option value="All">All Music Types</option>
          <option v-for="music in filterOptions.music" :key="music" :value="music">{{ music }}</option>
        </select>
        <select v-model="selectedDressCode" class="filter-select">
          <option value="All">All Dress Codes</option>
          <option v-for="dress in filterOptions.dress" :key="dress" :value="dress">{{ dress }}</option>
        </select>
        <select v-model="selectedGoodFor" class="filter-select">
          <option value="All">Good For...</option>
          <option v-for="goodFor in filterOptions.goodFor" :key="goodFor" :value="goodFor">{{ goodFor }}</option>
        </select>
      </div>
    </div>
    <section class="clubs-list">
      <div v-for="club in filteredClubs" :key="club.id" class="club-card">
        <a :href="club.slug" target="_blank" rel="noopener noreferrer" class="club-card-link">
          <img  v-lazy="club.logo" :alt="`${club.name} logo`" class="club-logo" loading="lazy" />
          <div class="club-content">
            <h2 class="club-name">{{ club.name }}</h2>
            <p class="club-caption">{{ club.caption }}</p>
            <div class="club-details">
              <span class="detail-item price-range" :title="`${club.price} out of 4`">{{ formatPriceRange(club.price) }}</span>
              <span class="detail-item opening-hours">{{ formatDisplayHours(club.tags) }}</span>
            </div>
            <div class="club-tags">
              <span v-if="getTagValue(club.tags, 'MUSIC')" class="tag music-tag">{{ getTagValue(club.tags, 'MUSIC') }}</span>
              <span v-if="getTagValue(club.tags, 'DRESS')" class="tag dress-tag">{{ getTagValue(club.tags, 'DRESS') }}</span>
              <span v-if="getTagValue(club.tags, 'GOOD FOR')" class="tag good-for-tag">{{ getTagValue(club.tags, 'GOOD FOR') }}</span>
            </div>
            <hr>
            <p>{{club.directions.address}}</p>

          </div>
        </a>
      </div>
      <div v-if="filteredClubs.length === 0" class="no-results">
        <p>No clubs found matching your search and filter criteria. Try adjusting your selections!</p>
      </div>
    </section>
  </div>
</template>

<script>
import CLUBS_CONFIG from '../../config/tulum-clubs-config.json'
export default {
  name: 'TulumClubsDirectory', // Updated component name for clarity
  metaInfo() {
    const pageUrl = `${this.baseUrl}/tulum-clubs-directory`; // Define the new canonical URL once
    const ldJsonScript = {
      type: 'application/ld+json',
      json: this.collectionPageSchema // The generated schema object
    };
    const scripts = this.collectionPageSchema ? [ldJsonScript] : [];

    return {
      title: 'Tulum Clubs Directory | Clubs Listing', // Slightly updated title
      script: scripts, // Array of script tags
      __dangerouslyDisableSanitizers: ['script'],
      link: [
        { rel: 'canonical', href: pageUrl }
      ],
      meta: [
        { hid: 'description', name: 'description', content: 'The complete A-Z directory of all clubs, beach parties, and nightlife venues in Tulum, Mexico. Search and filter to discover your perfect spot.' },
        // Open Graph / Facebook
        { hid: 'og:title', property: 'og:title', content: 'Tulum Clubs Directory & Nightlife Guide' },
        { hid: 'og:description', property: 'og:description', content: 'The complete A-Z directory of all clubs and nightlife venues in Tulum, Mexico.' },
        { hid: 'og:image', property: 'og:image', content: 'https://imgix.cosmicjs.com/2bc0e300-0c00-11f0-8617-41c4c9731576-659656992763397.png' },
        { hid: 'og:url', property: 'og:url', content: pageUrl },
        // Twitter
        { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { hid: 'twitter:title', name: 'twitter:title', content: 'Tulum Clubs Directory & Nightlife Guide' },
        { hid: 'twitter:description', name: 'twitter:description', content: 'The complete A-Z directory of all clubs and nightlife venues in Tulum, Mexico.' },
        { hid: 'twitter:image', name: 'twitter:image', content: 'https://www.tulum.party/path-to-your-featured-image.jpg' }
      ],
    };
  },
  data() {
    return {
      searchQuery: '',
      selectedMusic: 'All',
      selectedDressCode: 'All',
      selectedGoodFor: 'All',
      clubsConfig: CLUBS_CONFIG,
      baseUrl: 'https://www.tulum.party',
      primaryColor: '#ddd3d3',
      secondaryColor: '#aeaca8',
    };
  },
  computed: {
    themeStyles() {
      return {
        '--primary-color': this.primaryColor,
        '--secondary-color': this.secondaryColor
      };
    },
    activeClubsList() {
      return Object.entries(this.clubsConfig)
        .map(([id, clubData]) => ({ id, ...clubData }))
        .filter(club => !club.closed)

        ;
    },
    filterOptions() {
      const music = new Set();
      const dress = new Set();
      const goodFor = new Set();

      this.activeClubsList.forEach(club => {
        if (club.tags) {
          const musicTag = club.tags.find(t => t.label === 'MUSIC');
          if (musicTag && musicTag.value) {
            musicTag.value.split('/').forEach(m => music.add(m.trim().toUpperCase()));
          }

          const dressTag = club.tags.find(t => t.label === 'DRESS');
          if (dressTag && dressTag.value) dress.add(dressTag.value.trim().toUpperCase());

          const goodForTag = club.tags.find(t => t.label === 'GOOD FOR');
          if (goodForTag && goodForTag.value) goodFor.add(goodForTag.value.trim().toUpperCase());
        }
      });

      return {
        music: Array.from(music).sort(),
        dress: Array.from(dress).sort(),
        goodFor: Array.from(goodFor).sort(),
      };
    },
    filteredClubs() {
      let clubs = this.activeClubsList;

      if (this.searchQuery) {
        const lowerCaseQuery = this.searchQuery.toLowerCase();
        clubs = clubs.filter(club => club.name.toLowerCase().includes(lowerCaseQuery));
      }

      if (this.selectedMusic !== 'All') {
        clubs = clubs.filter(club => {
          const musicTag = club.tags && club.tags.find(t => t.label === 'MUSIC');
          return musicTag && musicTag.value.toUpperCase().includes(this.selectedMusic);
        });
      }

      if (this.selectedDressCode !== 'All') {
        clubs = clubs.filter(club => {
          const dressTag = club.tags && club.tags.find(t => t.label === 'DRESS');
          return dressTag && dressTag.value.toUpperCase() === this.selectedDressCode;
        });
      }

      if (this.selectedGoodFor !== 'All') {
        clubs = clubs.filter(club => {
          const goodForTag = club.tags && club.tags.find(t => t.label === 'GOOD FOR');
          return goodForTag && goodForTag.value.toUpperCase() === this.selectedGoodFor;
        });
      }

      return clubs;
    },
    collectionPageSchema() {
      const itemListElement = this.activeClubsList.map((club, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: this.generateNightClubSchema(club)
        };
      });

      return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Tulum Clubs Directory and Nightlife Guide',
        description: 'A complete, searchable directory of all nightclubs, beach clubs, and party venues in Tulum, Mexico.',
        url: `${this.baseUrl}/tulum-clubs-directory`,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: itemListElement
        }
      };
    }
  },
  methods: {
    formatDisplayHours(tags) {
      if (!tags) return 'Hours not available';
      const dayTag = tags.find(t => t.label === 'DAY');
      const nightTag = tags.find(t => t.label === 'NIGHT');
      let parts = [];
      if (dayTag && !dayTag.value.includes('--')) parts.push(`Day: ${dayTag.value}`);
      if (nightTag && !nightTag.value.includes('--')) parts.push(`Night: ${nightTag.value}`);
      return parts.length > 0 ? parts.join(' | ') : 'Special Events Only';
    },
    getTagValue(tags, label) {
      if (!tags) return '';
      const tag = tags.find(t => t.label === label);
      return tag ? tag.value : '';
    },
    generateNightClubSchema(club) {
      const clubUrl = `${this.baseUrl}${club.slug}`;
      const geo = club.directions ? this.extractCoordinates(club.directions.googleMap) : null;
      const openingHours = club.tags ? this.generateOpeningHours(club.tags) : [];

      return {
        '@type': 'NightClub',
        '@id': `${clubUrl}#entity`,
        name: club.name,
        url: clubUrl,
        image: club.logo,
        priceRange: this.formatPriceRange(club.price),
        address: {
          '@type': 'PostalAddress',
          streetAddress: club.directions ? club.directions.address : '',
          addressLocality: 'Tulum',
          addressRegion: club.addressRegion,
          postalCode: club.postalCode,
          addressCountry: 'MX'
        },
        ...(geo && { geo }),
        ...(openingHours.length > 0 && { openingHoursSpecification: openingHours })
      };
    },
    extractCoordinates(mapString) {
      if (!mapString) return null;
      const latRegex = /!3d([0-9-.]+)!/;
      const lngRegex = /!2d([0-9-.]+)!/;
      const latMatch = mapString.match(latRegex);
      const lngMatch = mapString.match(lngRegex);

      if (latMatch && latMatch[1] && lngMatch && lngMatch[1]) {
        return {
          '@type': 'GeoCoordinates',
          latitude: parseFloat(latMatch[1]),
          longitude: parseFloat(lngMatch[1])
        };
      }
      return null;
    },
    formatPriceRange(price) {
      if (price >= 1 && price <= 4) {
        return '$'.repeat(price);
      }
      return '$$$';
    },
    parseTime(timeStr) {
      if (!timeStr || timeStr.includes('--') || !timeStr.match(/(\d+)(AM|PM)?/)) return null;

      timeStr = timeStr.toUpperCase().trim();
      let [time, modifier] = timeStr.match(/(\d+)(AM|PM)?/).slice(1);
      let [hours] = time.split(':');
      hours = parseInt(hours, 10);

      if (modifier === 'PM' && hours < 12) {
        hours += 12;
      }
      if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }
      return `${String(hours).padStart(2, '0')}:00`;
    },
    generateOpeningHours(tags) {
      const dayTag = tags.find(t => t.label === 'DAY');
      const nightTag = tags.find(t => t.label === 'NIGHT');
      const openingHours = [];
      const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

      if (dayTag && dayTag.value && !dayTag.value.includes('--')) {
        const [opens, closes] = dayTag.value.split('-');
        const opensTime = this.parseTime(opens);
        const closesTime = this.parseTime(closes);
        if (opensTime && closesTime) {
          openingHours.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: allDays,
            opens: opensTime,
            closes: closesTime,
            description: "Day Club / Beach Club Hours"
          });
        }
      }

      if (nightTag && nightTag.value && !nightTag.value.includes('--')) {
        const [opens, closesRaw] = nightTag.value.split('-');
        const opensTime = this.parseTime(opens);

        let closesTime = this.parseTime(closesRaw);
        if (closesRaw.toUpperCase().includes('LATE')) {
          closesTime = '02:00';
        } else if (!closesTime) {
          closesTime = '02:00';
        }

        if (opensTime && closesTime) {
          openingHours.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: allDays,
            opens: opensTime,
            closes: closesTime,
            description: "Nightclub / Evening Event Hours"
          });
        }
      }

      return openingHours;
    }
  }
};
</script>

<style scoped>
:root {
  --primary-color: #665454;
  --secondary-color: #A08A5D;
}

.clubs-listing-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}
.page-header {
  text-align: center;
  margin-bottom: 40px;
}
.page-header h1 {
  font-size: 2.5em;
  color: var(--primary-color);
  margin-bottom: 10px;
}
.page-header p {
  font-size: 1.2em;
  color: #666;
}
.filters-container {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 40px;
}
.search-input {
  width: 100%;
  padding: 15px;
  font-size: 1.1em;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-bottom: 15px;
}
.filter-dropdowns {
  display: flex;
  gap: 15px;
  flex-wrap: wrap; /* Allows filters to wrap on smaller screens */
}
.filter-select {
  flex-grow: 1;
  padding: 10px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
}
/* New List Styles */
.clubs-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.club-card {
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: #1a1818;

  transition: box-shadow 0.3s ease;
  /*background-color: #fff;*/
  display: flex; /* Key for list layout */
}
.club-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}
.club-card-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  width: 100%;
  padding: 15px;
  gap: 20px;
  align-items: center;
}
.club-logo {
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 300px;
  flex-shrink: 0;
}
.club-content {
  flex-grow: 1;
}
.club-name {
  font-size: 1.5em;
  font-weight: bold;
  color: var(--primary-color);
  margin: 0 0 5px 0;
}
.club-caption {
  font-size: 1em;
  color: #555;
  margin-bottom: 15px;
}
.club-details {
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 15px;
  font-size: 0.9em;
  color: #333;
}
.detail-item {
  display: inline-block;
  padding: 4px 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
}
.price-range {
  font-weight: bold;
  font-size: 1.2em;
  color: var(--secondary-color);
  background-color: transparent;
}
.club-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.tag {
  font-size: 0.8em;
  padding: 5px 10px;
  border-radius: 12px;
  background-color: #e9e9e9;
  font-weight: 500;
}
.music-tag {
  background-color: #e0f2f1;
  color: #00796b;
}
.dress-tag {
  background-color: #f3e5f5;
  color: #8e24aa;
}
.good-for-tag {
  background-color: #fff3e0;
  color: #ef6c00;
}
.no-results {
  text-align: center;
  padding: 40px;
  font-size: 1.2em;
  color: #888;
}

@media (max-width: 600px) {
  .club-card-link {
    flex-direction: column;
    align-items: flex-start;
  }
  .filter-dropdowns {
    flex-direction: column;
  }
}
</style>
